/**
 * Get contrasting text color (black or white) based on background color
 * Uses the relative luminance formula to determine the best contrast
 * @param {string} hexColor - Hex color string (e.g., "#FF0000")
 * @returns {string} - Either "#000000" or "#FFFFFF"
 */
export function getContrastingColor(hexColor) {
  const hex = hexColor.replace('#', '');

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  // Calculate luminance using relative luminance formula
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return black for light backgrounds, white for dark backgrounds
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

/**
 * Convert hex color to RGB values
 * @param {string} hex - Hex color string (e.g., "#FF0000")
 * @returns {{ r: number, g: number, b: number }} RGB values
 */
export function hexToRgb(hex) {
  const cleanHex = hex.replace('#', '');
  return {
    r: parseInt(cleanHex.slice(0, 2), 16),
    g: parseInt(cleanHex.slice(2, 4), 16),
    b: parseInt(cleanHex.slice(4, 6), 16),
  };
}

/**
 * Convert RGB values to hex color string
 * @param {number} r - Red value (0-255)
 * @param {number} g - Green value (0-255)
 * @param {number} b - Blue value (0-255)
 * @returns {string} Hex color string (e.g., "#FF0000")
 */
export function rgbToHex(r, g, b) {
  /**
   * Convert a value to hex string
   * @param {number} value - Value to convert
   * @returns {string} Hex string (e.g., "0F")
   */
  const toHex = (value) => {
    const hex = value.toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

/**
 * Check if a color string is a valid hex color
 * @param {string} color - Color string to validate
 * @returns {boolean} True if valid hex color
 */
export function isValidHexColor(color) {
  const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  return hexRegex.test(color);
}
